import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { useDeviceLayout } from '@vio/atlas/context/deviceLayout';
import { getAllFilterKeys } from 'components/Filters/utils';
import { ScrollButton } from 'components/Swiper/ScrollButton';
import { useElementSize } from 'hooks/useElementSize';
import { useFiltersForm } from 'hooks/useFiltersForm';
import throttle from 'lodash/throttle';
import { getIsRtlLanguage } from 'modules/meta/selectors';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
const Wrapper = /*#__PURE__*/styled('section')({
  name: "Wrapper",
  class: "wrjer8q",
  propsAsIs: false
});
const ScrollWrapper = /*#__PURE__*/styled('div')({
  name: "ScrollWrapper",
  class: "sq885dg",
  propsAsIs: false
});
type ActionType = 'NEXT' | 'PREV';
const allFilterKeys = getAllFilterKeys();
const THROTTLING_INTERVAL = 500; // ms

/**
 * Wrapper used to handle the horizontal scrolling on the filters bar
 */
interface Props {
  children: React.ReactNode;
  left?: number;
  right?: number;
  // Threshold for scroll position
  threshold?: number;
}
export const FiltersScrollWrapper: React.FC<Props> = ({
  children,
  left,
  right,
  threshold = 200
}) => {
  const isRtl = useSelector(getIsRtlLanguage);
  const {
    elementRef,
    size: {
      width: wrapperWidth,
      el: wrapperElement
    }
  } = useElementSize<HTMLDivElement>();
  const [trackingEventFired, setTrackingEventFired] = useState(false);
  const [scrollableOffset, setScrollableOffset] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const {
    isMobile
  } = useDeviceLayout();
  const dispatch = useDispatch();
  const {
    getFilters
  } = useFiltersForm(allFilterKeys);
  const filters = getFilters();
  useEffect(() => {
    // track if the bar is scrollable by default or becomes scrollable
    // should only fire once per render
    if (!trackingEventFired && scrollableOffset > 0) {
      dispatch(trackEvent({
        category: Category.System,
        entity: Entity.FilterBarScrollButton,
        action: Action.Hidden
      }));
      setTrackingEventFired(true);
    }
  }, [dispatch, scrollableOffset, trackingEventFired]);
  useEffect(() => {
    if (wrapperElement) {
      const roundedWrapperWidth = Math.ceil(wrapperWidth);
      const newScrollableOffset = roundedWrapperWidth > 0 ? Math.ceil(wrapperElement.scrollWidth) - roundedWrapperWidth : 0;
      if (scrollableOffset !== newScrollableOffset) {
        setScrollableOffset(Math.round(newScrollableOffset));
      }
    }
  }, [filters, scrollableOffset, wrapperWidth, scrollPosition, wrapperElement]);
  useEffect(() => {
    const ref = wrapperElement;
    if (ref) {
      setScrollWidth(Math.ceil(ref.scrollWidth));
      const onScroll = throttle(() => {
        setScrollPosition(Math.round(ref.scrollLeft));
      }, THROTTLING_INTERVAL);
      ref.addEventListener('scroll', onScroll);
      return () => ref.removeEventListener('scroll', onScroll);
    }
  }, [wrapperElement]);
  const getWidthToScroll = useCallback((direction: ActionType) => {
    const realScrollPosition = isRtl ? -scrollPosition : scrollPosition;
    if (direction === 'NEXT') {
      if (scrollWidth >= realScrollPosition + wrapperWidth) return Math.ceil(realScrollPosition + wrapperWidth) - threshold;
      return scrollableOffset;
    }
    if (realScrollPosition + threshold >= wrapperWidth) return Math.ceil(realScrollPosition - wrapperWidth + threshold);
    return 0;
  }, [isRtl, scrollPosition, scrollWidth, scrollableOffset, wrapperWidth, threshold]);
  const isButtonVisible = useCallback((direction: ActionType): boolean => {
    if (isRtl) {
      if (direction === 'NEXT') return -scrollPosition < scrollableOffset;
      return scrollPosition < 0;
    }
    if (direction === 'NEXT') return scrollPosition < scrollableOffset;
    return scrollPosition > 0;
  }, [isRtl, scrollPosition, scrollableOffset]);
  const onArrowClick = useCallback((direction: ActionType) => {
    if (!isButtonVisible(direction)) return;
    const widthToScroll = getWidthToScroll(direction);
    wrapperElement.scroll({
      left: (isRtl ? -1 : 1) * widthToScroll,
      behavior: 'smooth'
    });
  }, [wrapperElement, getWidthToScroll, isRtl, isButtonVisible]);
  return <Wrapper>
      {!isMobile && <>
          <ScrollButton direction="left" isVisible={isButtonVisible('PREV')} onClick={() => onArrowClick('PREV')} left={left} isRtl={isRtl} />
          <ScrollButton direction="right" isVisible={isButtonVisible('NEXT')} onClick={() => onArrowClick('NEXT')} right={right} isRtl={isRtl} />
        </>}
      <ScrollWrapper ref={elementRef}>{children}</ScrollWrapper>
    </Wrapper>;
};

require("./FiltersScrollWrapper.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./FiltersScrollWrapper.tsx");